import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';

import { apiUrl } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';

/**
 * Web realtime socket — connects to the server's authenticated `/rt` namespace
 * (same one the mobile app + realtimeDispatcher use). Single long-lived
 * connection; components subscribe to events via on(event).
 *
 * Server wraps every event as { event, data, ts }. onAny() streams them into
 * rawEvents$; on(name) filters out the ones a caller wants.
 *
 * NOTE: imports socket.io-client directly (a dependency of ngx-socket-io which
 * the project already uses). If the build can't resolve it, run:
 *   npm i socket.io-client
 */
interface ServerEnvelope { event: string; data: any; ts: string; }

@Injectable({ providedIn: 'root' })
export class SocketService {

  private socket: Socket | null = null;
  private connecting = false;

  /** All incoming /rt events. */
  private readonly rawEvents$ = new Subject<ServerEnvelope>();
  readonly events$ = this.rawEvents$.asObservable();

  private readonly _connected$ = new Subject<boolean>();
  readonly connected$ = this._connected$.asObservable();

  constructor(private authService: AuthService) {}

  get isConnected(): boolean { return !!this.socket && this.socket.connected; }

  /** Connect (idempotent). Fetches the JWT from AuthService for the handshake. */
  async connect(): Promise<void> {
    if (this.isConnected || this.connecting) return;
    let token = '';
    try {
      const ud: any = await this.authService.getUserDetails();
      token = ud && ud.token ? ud.token : '';
    } catch { /* not logged in */ }
    if (!token) return;

    // apiUrl = 'https://host/api' → connect to origin/rt with path /api/socket.io
    const parsed = new URL(apiUrl);
    const origin = parsed.origin;
    const path   = parsed.pathname.replace(/\/$/, '') + '/socket.io';

    if (this.socket) { try { this.socket.removeAllListeners(); this.socket.disconnect(); } catch {} }

    this.connecting = true;
    this.socket = io(`${origin}/rt`, {
      path,
      transports:           ['websocket', 'polling'],
      auth:                 { token },
      reconnection:         true,
      reconnectionAttempts: Infinity,
      reconnectionDelay:    1000,
      reconnectionDelayMax: 30000,
      timeout:              20000,
      forceNew:             true,
    });

    this.socket.on('connect', () => {
      this.connecting = false;
      this._connected$.next(true);
      console.log('[WebSocket] ✅ connected to /rt');
    });
    this.socket.on('disconnect', (reason: string) => {
      this._connected$.next(false);
      console.log('[WebSocket] disconnected:', reason);
    });
    this.socket.on('connect_error', (err: any) => {
      this.connecting = false;
      this._connected$.next(false);
      console.warn('[WebSocket] connect_error:', err && err.message);
    });

    // Stream every server event into rawEvents$
    this.socket.onAny((eventName: string, payload: ServerEnvelope) => {
      if (!payload || typeof payload !== 'object') return;
      this.rawEvents$.next({
        event: payload.event || eventName,
        data:  payload.data  || {},
        ts:    payload.ts,
      });
    });
  }

  /** Subscribe to a specific event's data payload. */
  on<T = any>(eventName: string): Observable<T> {
    return this.events$.pipe(
      filter((e) => e.event === eventName),
      map((e) => e.data as T),
    );
  }

  disconnect(): void {
    if (!this.socket) return;
    try { this.socket.removeAllListeners(); this.socket.disconnect(); } catch {}
    this.socket = null;
    this.connecting = false;
    this._connected$.next(false);
  }
}
